import React from "react";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard,
  Activity,
  Settings,
  Briefcase,
  Search,
  Terminal,
  Radio,
} from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { cn } from "@/lib/utils";

interface LayoutProps {
  children: React.ReactNode;
}

const SidebarItem = ({
  href,
  icon: Icon,
  label,
  badge,
}: {
  href: string;
  icon: React.ElementType;
  label: string;
  badge?: string | number;
}) => {
  const [location] = useLocation();
  const isActive = location === href;

  return (
    <Link
      href={href}
      className={cn(
        "flex items-center justify-between px-3 py-1.5 text-sm transition-colors rounded-sm",
        isActive
          ? "bg-foreground text-background font-medium"
          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      {badge !== undefined ? (
        <span
          className={cn(
            "text-[10px] font-mono px-1.5 rounded-sm",
            isActive
              ? "bg-background text-foreground"
              : "bg-secondary text-foreground"
          )}
        >
          {badge}
        </span>
      ) : null}
    </Link>
  );
};

export function Layout({ children }: LayoutProps) {
  const jobs = useQuery(api.jobs.list, {});
  const jobsCount = jobs?.length ?? 0;
  const badgeText = jobsCount >= 99 ? "99+" : jobsCount;

  return (
    <div className="flex h-screen w-full bg-background font-sans text-foreground overflow-hidden">
      {/* Sidebar - Pure Utility */}
      <aside className="w-[200px] border-r border-border flex flex-col shrink-0 bg-card relative z-10 shadow-lg">
        <div className="h-16 px-4 border-b border-border flex items-center">
          <div className="flex items-center gap-2 font-bold text-sm tracking-tight">
            <Terminal className="w-4 h-4" />
            <span>CURATOR_V1</span>
          </div>
        </div>

        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          <SidebarItem
            href="/"
            icon={Briefcase}
            label="Feed"
            badge={badgeText}
          />
          <SidebarItem href="/broadcasts" icon={Radio} label="Broadcasts" />
          <SidebarItem href="/scraper-status" icon={Activity} label="Sources" />
          <SidebarItem href="/settings" icon={Settings} label="Config" />
        </nav>

        <div className="p-2 border-t border-border text-[10px] text-muted-foreground font-mono text-center">
          INTERNAL USE ONLY
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-hidden min-h-0">{children}</div>
      </main>
    </div>
  );
}
